import { useEffect, useState } from "react";
import { Button, Card, Form, Nav, Tab } from "react-bootstrap";
import { toast } from "react-toastify";
import { SettingsService } from "Services/settingsService";
import { renderMarkdown } from "./markdownHelper";

export default function SettingsIndex() {
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [dashboardTitle, setDashboardTitle] = useState("");
    const [dashboardContent, setDashboardContent] = useState("");
    const [saving, setSaving] = useState(false);

    const fetchSettings = async () => {
        try {
            const res = await SettingsService.get();
            if (res.status === 200 && res.data.data) {
                setTitle(res.data.data.instructionTitle || "");
                setContent(res.data.data.instructionContent || "");
                setDashboardTitle(res.data.data.dashboardInstructionTitle || "");
                setDashboardContent(res.data.data.dashboardInstructionContent || "");
            }
        } catch (e) {
            toast.error("Failed to load settings");
        }
    };

    useEffect(() => { fetchSettings(); }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await SettingsService.update({
                instructionTitle: title,
                instructionContent: content,
                dashboardInstructionTitle: dashboardTitle,
                dashboardInstructionContent: dashboardContent,
            });
            if (res.status === 200) {
                toast.success("Instructions saved successfully");
            }
        } catch (e) {
            toast.error("Failed to save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="shadow-sm mt-4" style={{ border: "none" }}>
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Candidate Instructions</h5>
                    <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </div>

                <Tab.Container defaultActiveKey="instruction1">
                    <Nav variant="pills" className="mb-3">
                        <Nav.Item>
                            <Nav.Link eventKey="instruction1">Instruction 1</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="instruction2">Instruction 2</Nav.Link>
                        </Nav.Item>
                    </Nav>

                    <Tab.Content>
                        <Tab.Pane eventKey="instruction1">
                            <InstructionEditor
                                hint="This content is shown to candidates on the instructions page after login. Supports Markdown formatting."
                                titlePlaceholder="e.g. Fetal Radiology Skill Certificate (FRSC)"
                                title={title}
                                onTitleChange={setTitle}
                                content={content}
                                onContentChange={setContent}
                            />
                        </Tab.Pane>
                        <Tab.Pane eventKey="instruction2">
                            <InstructionEditor
                                hint="This content is shown to candidates in a popup when they open the exam dashboard. Supports Markdown formatting."
                                titlePlaceholder="e.g. Before You Begin"
                                title={dashboardTitle}
                                onTitleChange={setDashboardTitle}
                                content={dashboardContent}
                                onContentChange={setDashboardContent}
                            />
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            </Card.Body>
        </Card>
    );
}

interface InstructionEditorProps {
    hint: string;
    titlePlaceholder: string;
    title: string;
    onTitleChange: (value: string) => void;
    content: string;
    onContentChange: (value: string) => void;
}

function InstructionEditor({
    hint, titlePlaceholder, title, onTitleChange, content, onContentChange,
}: InstructionEditorProps) {
    return (
        <>
            <p className="text-muted small mb-3">{hint}</p>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Title</Form.Label>
                <Form.Control
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    placeholder={titlePlaceholder}
                />
            </Form.Group>

            <Form.Label className="fw-bold">Content (Markdown)</Form.Label>
            <Tab.Container defaultActiveKey="write">
                <Nav variant="tabs" className="mb-0">
                    <Nav.Item>
                        <Nav.Link eventKey="write">Write</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="preview">Preview</Nav.Link>
                    </Nav.Item>
                </Nav>
                <Tab.Content>
                    <Tab.Pane eventKey="write" className="pt-2">
                        <Form.Control
                            as="textarea"
                            rows={16}
                            value={content}
                            onChange={(e) => onContentChange(e.target.value)}
                            placeholder={"Write instructions in Markdown...\n\n# Heading\n**Bold text**\n*Italic text*\n\nParagraphs separated by blank lines."}
                            style={{ fontFamily: "monospace", fontSize: 13 }}
                        />
                    </Tab.Pane>
                    <Tab.Pane eventKey="preview" className="pt-2">
                        <div
                            className="border rounded p-3"
                            style={{ minHeight: 200, fontSize: 14, lineHeight: 1.7 }}
                            dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
                        />
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            <Form.Text className="text-muted mt-2 d-block">
                Supported: # Headings, **bold**, *italic*, blank lines for paragraphs, - lists
            </Form.Text>
        </>
    );
}
